import { arrayMove } from '@dnd-kit/sortable';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReorderPlaylist } from '~/api/playlists';
import { Count } from '~/components/count';
import { SearchBar } from '~/components/forms/search-bar';
import { SearchContainer } from '~/components/search';
import { formatRuntime } from '~/lib/runtime';
import { PlaylistItemDetail } from './PlaylistItemDetail';
import { PlaylistReorderList } from './PlaylistReorderList';
import { EMPTY_ITEMS } from './PlaylistReorderList/constants';
import type { PlaylistDesign__Playlist as Playlist } from './index.generated';

// --- scrolling helper (works with nested scrollers & sticky headers) --------
const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
  let p = node?.parentElement;
  while (p) {
    const { overflowY } = getComputedStyle(p);
    if (/(auto|scroll|overlay)/.test(overflowY) && p.scrollHeight > p.clientHeight) return p;
    p = p.parentElement;
  }
  return null;
};

const scrollRowIntoViewBottom = (row: HTMLElement) => {
  const scroller = getScrollParent(row);
  const margin = 12;
  if (scroller) {
    const rowRect = row.getBoundingClientRect();
    const scRect = scroller.getBoundingClientRect();
    const overflow = rowRect.bottom - (scRect.bottom - margin);
    if (overflow > 0) scroller.scrollBy({ top: overflow, behavior: 'smooth' });
  } else {
    const rect = row.getBoundingClientRect();
    const overflow = rect.bottom - (window.innerHeight - margin);
    if (overflow > 0) window.scrollBy({ top: overflow, behavior: 'smooth' });
  }
};

export type MoveDir = 'top' | 'up' | 'down' | 'bottom';

interface PlaylistContainerProps {
  banner?: ReactNode; // optional slot above the toolbar, inside left pane
  bulkMenu?: ReactNode;
  loading?: boolean;
  onRemoveContent: (playlistItemId: number) => void;
  playlist?: Playlist;
  readOnly?: boolean;
  selectedIds: number[];
  setSelectedIds: (ids: number[]) => void;
  toolbarActions?: ReactNode;
}

export const PlaylistContainer = ({
  banner,
  bulkMenu,
  loading = false,
  playlist,
  onRemoveContent,
  readOnly = false,
  selectedIds,
  setSelectedIds,
  toolbarActions,
}: PlaylistContainerProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [search, setSearch] = useState('');

  const [focusedId, setFocusedId] = useState<number | null>();

  const [reorder] = useReorderPlaylist();

  // Animation frame cleanup
  const rafRef = useRef<number>();

  const items = playlist?.playlistItems ?? EMPTY_ITEMS;

  const focusedItem = focusedId != null ? items.find((i) => i.id === focusedId) : undefined;

  const [order, setOrder] = useState<number[]>(() => items.map((i) => i.id));

  // Keep order in sync as playlist changes (including undefined -> defined and vice-versa)
  useEffect(() => {
    setOrder(items.map((i) => i.id));
  }, [items]);

  useEffect(() => {
    // If focused item gets deleted, clear focus (which will hide panel)
    if (focusedId != null && !order.includes(focusedId)) {
      setFocusedId(null);
    }
  }, [focusedId, order]);

  // Clean up animation frame on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const persistOrder = useCallback(
    async (next: number[]) => {
      setOrder(next);
      if (!readOnly && playlist?.id) {
        await reorder({
          variables: { playlistId: playlist.id, playlistItemIds: next },
        });
      }
    },
    [playlist?.id, reorder, readOnly],
  );

  const moveItem = useCallback(
    async (id: number, where: MoveDir) => {
      if (readOnly) return;
      const i = order.indexOf(id);
      if (i === -1) return;

      let next = order;
      if (where === 'top' && i > 0) next = [id, ...order.filter((x) => x !== id)];
      if (where === 'bottom' && i < order.length - 1) next = [...order.filter((x) => x !== id), id];
      if (where === 'up' && i > 0) next = arrayMove(order, i, i - 1);
      if (where === 'down' && i < order.length - 1) next = arrayMove(order, i, i + 1);
      if (next === order) return;

      await persistOrder(next);
      setFocusedId(id);

      // if moved to last, ensure it's visible
      if (next[next.length - 1] === id) {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(() => {
          const el = document.querySelector<HTMLElement>(`[data-row-id="${String(id)}"]`);
          if (el) scrollRowIntoViewBottom(el);
        });
      }
    },
    [order, persistOrder, readOnly],
  );

  const onDragReorder = useCallback(
    (activeId: number, overId: number) => {
      if (readOnly || activeId === overId) return;

      const oldIndex = order.indexOf(activeId);
      if (oldIndex < 0) return;

      const newIndex = order.indexOf(overId);
      if (newIndex < 0 || newIndex === oldIndex) return;

      const next = arrayMove(order, oldIndex, newIndex);
      void persistOrder(next);
    },
    [order, persistOrder, readOnly],
  );

  const canEdit = useMemo(() => !readOnly && !!playlist, [playlist, readOnly]);

  const moveFlags = useMemo(() => {
    const idx = focusedId != null ? order.indexOf(focusedId) : -1;
    return {
      canMoveTop: canEdit && idx > 0,
      canMoveUp: canEdit && idx > 0,
      canMoveDown: canEdit && idx !== -1 && idx < order.length - 1,
      canMoveBottom: canEdit && idx !== -1 && idx < order.length - 1,
    };
  }, [focusedId, order, canEdit]);

  const runtime = useMemo(() => {
    if (!items.length) return '0s';
    return formatRuntime(playlist?.runtime ?? null);
  }, [playlist?.runtime, items.length]);

  const handleFocus = useCallback(
    (id: number) => {
      setFocusedId(id);
      setSelectedIds([]);
    },
    [setSelectedIds],
  );

  const handleCheck = useCallback((ids: number[]) => setSelectedIds(ids), [setSelectedIds]);

  const handleClosePanel = useCallback(() => {
    setFocusedId(null);
  }, []);

  const totalCount = items.length;
  const hasSelection = selectedIds.length > 0;

  const gridTemplateColumns = useMemo(
    () => ({
      xs: '1fr',
      lg: focusedItem ? 'minmax(0, 1fr) clamp(300px, 25%, 380px)' : '1fr',
    }),
    [focusedItem],
  );

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.default,
        borderTop: '1px solid',
        borderColor: theme.palette.borders.dark,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns,
          alignItems: 'start',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            p: 2,
            borderRight: { lg: focusedItem ? `1px solid ${theme.palette.borders.light}` : 'none' },
            minHeight: focusedItem ? { xs: 'auto', lg: '850px' } : 'auto',
          }}
        >
          {banner ? <Box sx={{ mb: 2 }}>{banner}</Box> : null}

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 1.5, md: 2 }}
            alignItems={{ xs: 'stretch', md: 'center' }}
            sx={{
              mb: { xs: 1.5 },
              gap: { md: 1 },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <SearchContainer sx={{ pb: 0 }}>
                <SearchBar
                  onChange={setSearch}
                  placeholder={t('components.playlist_container.search_placeholder')}
                  search={search}
                />
              </SearchContainer>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                flexShrink: 0,
                gap: 1,
              }}
            >
              {canEdit && hasSelection ? (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ sm: 'center' }}
                >
                  {bulkMenu}
                  <Count
                    selectedCount={selectedIds.length}
                    totalCount={totalCount}
                    thing="components.playlist_container.item_count_plural"
                  />
                </Stack>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Count
                    selectedCount={selectedIds.length}
                    totalCount={totalCount}
                    thing="components.playlist_container.item_count_plural"
                  />
                  <Typography variant="subtitle1">, {runtime}</Typography>
                </Box>
              )}
            </Box>

            {toolbarActions ? (
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  justifyContent: { xs: 'flex-start', md: 'flex-end' },
                  flexShrink: 0,
                  flexWrap: 'wrap',
                  rowGap: 1,
                }}
              >
                {toolbarActions}
              </Stack>
            ) : null}
          </Stack>

          <PlaylistReorderList
            focusedId={focusedId}
            loading={loading}
            onCheck={handleCheck}
            onDragReorder={onDragReorder}
            onRemove={onRemoveContent}
            onRowFocus={handleFocus}
            order={order}
            playlist={playlist}
            readOnly={readOnly || !playlist}
            search={search}
            selectedIds={selectedIds}
          />
        </Box>

        {/* Right: sticky detail panel (desktop only) */}
        {focusedItem && (
          <Box
            sx={(theme) => ({
              backgroundColor: theme.palette.background.default,
              borderRadius: { xs: theme.spacing(1), lg: 0 },
              boxShadow: {
                xs: '0 4px 20px rgba(0,0,0,0.15)',
                lg: 'none',
              },
              display: { xs: 'block', lg: 'flex' },
              maxHeight: { xs: 'calc(100vh - 130px)', lg: 'none' },
              maxWidth: { xs: '300px', lg: 'none' },
              minWidth: 0,
              overflowY: { xs: 'auto', lg: 'visible' },
              position: { xs: 'fixed', lg: 'sticky' },
              right: { xs: theme.spacing(2), lg: 'auto' },
              top: { xs: '130px', lg: '-20px' },
              width: { xs: '75%', lg: 'auto' },
              zIndex: { xs: 1000, lg: 'auto' },
            })}
          >
            <PlaylistItemDetail
              canEdit={canEdit}
              item={focusedItem}
              moveItem={moveItem}
              moveFlags={moveFlags}
              onClose={handleClosePanel}
              onRemove={onRemoveContent}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
